import { useEffect, useRef, useState } from 'react';
import styles from './Uploader.module.css';

interface UploaderProps {
  onFile: (file: File) => void;
  busy?: boolean;
  error?: string | null;
  /** The currently-loaded image, shown as a thumbnail once present. */
  preview?: { url: string; name: string } | null;
}

// Surface the right paste shortcut for the platform (best-effort, defaults to ⌘).
const PASTE_KEYS = /Mac|iPhone|iPad|iPod/i.test(
  typeof navigator !== 'undefined' ? navigator.platform || navigator.userAgent : '',
)
  ? '⌘V'
  : 'Ctrl+V';

/** A simple line-art upload glyph — currentColor, no dependency. */
function UploadIcon() {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 16V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

/** Drag/drop + file-pick + paste image input with inline, recoverable errors. */
export function Uploader({ onFile, busy = false, error, preview }: UploaderProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Paste an image from the clipboard anywhere on the page.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const item = Array.from(e.clipboardData?.items ?? []).find((it) =>
        it.type.startsWith('image/'),
      );
      const file = item?.getAsFile();
      if (file) onFile(file);
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [onFile]);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0]; // take the first image
    if (file) onFile(file);
  }

  return (
    <div className={styles.uploader}>
      <label
        className={
          `${styles.dropzone}` +
          (preview ? ` ${styles.compact}` : '') +
          (dragging ? ` ${styles.dropzoneActive}` : '')
        }
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className={styles.input}
          disabled={busy}
          aria-label={preview ? 'Change image' : 'Upload an image'}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = ''; // allow re-selecting the same file
          }}
        />

        {preview ? (
          <>
            <img className={styles.thumb} src={preview.url} alt="" />
            <span className={styles.meta}>
              <span className={styles.fileName}>{preview.name}</span>
              <span className={styles.hint}>
                {busy ? 'Reading colors…' : `Drop, paste (${PASTE_KEYS}), or click to change`}
              </span>
            </span>
            <span className={styles.change} aria-hidden="true">
              Change image
            </span>
          </>
        ) : (
          <>
            <UploadIcon />
            <span className={styles.label}>
              {busy ? 'Reading colors…' : 'Drop an image, paste, or click to choose'}
            </span>
            <span className={styles.hint}>PNG, JPG, GIF, WebP — up to 25&nbsp;MB</span>
            <span className={styles.hint}>Press {PASTE_KEYS} to paste from your clipboard</span>
          </>
        )}
      </label>
      {error ? (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
